import { useMemo } from 'react';

import { useApolloClient, useQuery } from '@apollo/client/react';

import {
  DoorToDoor__BundleEnum,
  StorageBundlesDocument,
} from '@graphql/platform';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { PLANS } from '@root/constants/pricing';
import { PlanName } from '@root/resources/types/plan_name';

import { useLastDefined } from '@utils/hooks/use_last_defined';

export interface ChallengerPackingAmountState {
  amount: number | undefined;
  loading: boolean;
  error?: Error;
}

const readPackingAmountFromCache = (
  client: ReturnType<typeof useApolloClient>,
  cuft: number | undefined,
  zip: string | undefined,
): number | undefined => {
  if (cuft == null || !zip) return undefined;

  try {
    const data = client.readQuery({
      query: StorageBundlesDocument,
      variables: {
        cuft,
        amountCuft: cuft,
        zip,
      },
    });
    const packingBundle = data?.bundles?.find(
      (bundle) => bundle.kind === DoorToDoor__BundleEnum.PickupAndPacking,
    );
    return packingBundle?.packageSetEntry?.amount ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Full-service packing price for the selected plan size + ZIP.
 *
 * Reads from the ZIP prefetch cache when available so the amount updates on
 * slider drop without waiting on a network round-trip.
 */
export const useChallengerPackingAmount = (
  planSizeOverride?: PlanName,
): ChallengerPackingAmountState => {
  const client = useApolloClient();
  const {
    flowState: {
      values: { planSize: flowPlanSize, zip },
    },
  } = useStorageCheckoutContext();

  const planSize = planSizeOverride ?? flowPlanSize;
  const cuft = planSize ? PLANS[planSize].cuft : undefined;
  const normalizedZip = zip?.trim();
  const skip = cuft == null || !normalizedZip;

  const cachedAmount = useMemo(
    () => readPackingAmountFromCache(client, cuft, normalizedZip),
    [client, cuft, normalizedZip],
  );

  const { data, loading, error } = useQuery(StorageBundlesDocument, {
    variables: {
      cuft,
      amountCuft: cuft ?? 0,
      zip: normalizedZip,
    },
    skip,
    fetchPolicy: 'cache-first',
  });

  const packingBundle = data?.bundles?.find(
    (bundle) => bundle.kind === DoorToDoor__BundleEnum.PickupAndPacking,
  );

  const amount =
    packingBundle?.packageSetEntry?.amount ?? cachedAmount ?? undefined;
  const stableAmount = useLastDefined(amount);

  return {
    amount: amount ?? stableAmount,
    loading: !skip && loading && amount == null && stableAmount == null,
    error: amount == null && stableAmount == null ? error : undefined,
  };
};
